<template>
  <v-container class="d-flex flex-column ga-3">
    <v-row>
      <v-col>
        <UsageExample v-model="tab" :code="code" :script="script" :tabs="tabs">
          <!-- Basic usage: icon list driven by :cs-nav-items -->
          <cs-nav-list
            v-if="tab === 'Basic'"
            id="cs-nav-list-basic"
            class="mx-auto"
            :cs-nav-items="csNavItems"
            @item-click="itemClick"
            @left-icon-click="leftIconClick"
            @right-icon-click="rightIconClick"
          />

          <!-- Slot mode: header + body slots, with useNavStack for drill-in navigation -->
          <cs-nav-list v-else id="cs-nav-list-slots" class="mx-auto" style="max-height: 480px">
            <template v-if="canGoBack" #header>
              <cs-nav-list-header
                :title="currentLevel.title"
                :left-icon="currentLevel.headerIcon"
                @back="popNav"
              />
            </template>

            <!-- A level can declare a body panel (component + props) to render;
                 otherwise it falls back to the default nav list. -->
            <component
              :is="currentLevel.body.component"
              v-if="currentLevel.body?.type === 'panel'"
              v-bind="currentLevel.body.props"
            />
            <cs-nav-list-items
              v-else
              :items="currentLevel.items"
              @item-click="drillInAt(currentLevel.items, $event)"
              @right-icon-click="drillInAt(currentLevel.items, $event)"
            />

            <template v-if="currentLevel.footer" #footer>
              <div class="cs-nav-list-demo__footer">
                <hx-button
                  :variant="currentLevel.footer.variant"
                  :label="currentLevel.footer.label"
                  :left-icon="currentLevel.footer.leftIcon"
                  block
                  @click="popNav"
                />
              </div>
            </template>
          </cs-nav-list>
        </UsageExample>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import UsageExample from '@/components/helix/utils/UsageExample';
import PageHistoryTrackerPanel from './PageHistoryTrackerPanel';
import TagsPanel from './TagsPanel';
import {
  CSNavList,
  CSNavListItems,
  CSNavListHeader,
  useNavStack
} from '@complispace/cs-design-system';

const BASIC_CODE = `
<cs-nav-list
  id="cs-nav-list-basic"
  :cs-nav-items="[
    { text: 'Page History', leftIcon: 'mdi-history', rightIcon: 'mdi-chevron-right' },
    { text: 'Tags', leftIcon: 'mdi-tag-outline', rightIcon: 'mdi-chevron-right' },
    { text: 'Document Management', leftIcon: 'mdi-file-document-outline', rightIcon: 'mdi-chevron-right' },
    { text: 'Workflows', leftIcon: 'mdi-sitemap-outline', rightIcon: 'mdi-chevron-right' }
  ]"
  @item-click="itemClick"
  @left-icon-click="leftIconClick"
  @right-icon-click="rightIconClick"
/>
`;

const SLOTS_CODE = `
<!-- Slot mode: supply your own header + body + footer and pair it with useNavStack
     for drill-in navigation. The header (back + title) only appears once you
     drill into a child level; footer actions sit outside the scrollable body.
     Drilling into "Page History" swaps the body for a version timeline. -->
<cs-nav-list id="cs-nav-list-slots" style="max-height: 480px">
  <template v-if="canGoBack" #header>
    <cs-nav-list-header
      :title="currentLevel.title"
      :left-icon="currentLevel.headerIcon"
      @back="popNav"
    />
  </template>

  <!-- A level can declare a body panel (component + props); otherwise it
       falls back to the default nav list. -->
  <component
    :is="currentLevel.body.component"
    v-if="currentLevel.body?.type === 'panel'"
    v-bind="currentLevel.body.props"
  />
  <cs-nav-list-items
    v-else
    :items="currentLevel.items"
    @item-click="drillInAt(currentLevel.items, $event)"
    @right-icon-click="drillInAt(currentLevel.items, $event)"
  />

  <template v-if="currentLevel.footer" #footer>
    <div class="cs-nav-list-demo__footer">
      <hx-button
        :variant="currentLevel.footer.variant"
        :label="currentLevel.footer.label"
        :left-icon="currentLevel.footer.leftIcon"
        block
        @click="popNav"
      />
    </div>
  </template>
</cs-nav-list>
`;

const SLOTS_SCRIPT = `
import { useNavStack } from '@complispace/cs-design-system';
import PageHistoryTrackerPanel from '@/components/cs-nav-list/PageHistoryTrackerPanel';
import TagsPanel from '@/components/cs-nav-list/TagsPanel';

const pageHistory = [ /* { title, subtitle, state, titleButton, subtitleButton } ... */ ];
const tags = [ /* { label, color, removable } ... */ ];

// Each row's 'child' is a frame. A frame is either a nested list ({ items }),
// a body panel ({ body: { type: 'panel', component, props } }), or a footer
// action ({ footer: { variant, label, leftIcon } }) rendered in #footer.
const navTree = [
  {
    text: 'Page History',
    leftIcon: 'mdi-history',
    rightIcon: 'mdi-chevron-right',
    child: {
      title: 'Page History',
      headerIcon: 'mdi-history',
      body: {
        type: 'panel',
        component: PageHistoryTrackerPanel,
        props: { items: pageHistory }
      },
      footer: {
        variant: 'primary',
        label: 'Return to current version',
        leftIcon: 'mdi-backup-restore'
      }
    }
  },
  {
    text: 'Tags',
    leftIcon: 'mdi-tag-outline',
    rightIcon: 'mdi-chevron-right',
    child: {
      title: 'Tags',
      headerIcon: 'mdi-tag-outline',
      body: {
        type: 'panel',
        component: TagsPanel,
        props: { tags }
      }
    }
  },
  {
    text: 'Document Management',
    leftIcon: 'mdi-file-document-outline',
    rightIcon: 'mdi-chevron-right',
    child: {
      title: 'Document Management',
      headerIcon: 'mdi-file-document-outline',
      items: [
        {
          text: 'Policies',
          leftIcon: 'mdi-shield-check-outline',
          href: '#'
        },
        {
          text: 'Procedures',
          leftIcon: 'mdi-clipboard-text-outline',
          href: '#'
        },
        // Long labels truncate with an ellipsis and reveal the full text in a tooltip.
        {
          text: 'Child Safety and Wellbeing Policy Framework 2024',
          leftIcon: 'mdi-file-document-outline',
          href: '#'
        }
      ]
    }
  },
  {
    text: 'Workflows',
    leftIcon: 'mdi-sitemap-outline',
    rightIcon: 'mdi-chevron-right',
    child: {
      title: 'Workflows',
      headerIcon: 'mdi-sitemap-outline',
      items: [
        {
          text: 'Approvals',
          leftIcon: 'mdi-check-decagram-outline',
          href: '#'
        }
      ]
    }
  },
  // Long label at the root level also truncates + shows a tooltip.
  {
    text: 'Records, Audits and Compliance Management',
    leftIcon: 'mdi-clipboard-text-outline',
    rightIcon: 'mdi-chevron-right',
    child: {
      title: 'Records, Audits and Compliance Management',
      headerIcon: 'mdi-clipboard-text-outline',
      items: [
        {
          text: 'Annual report',
          leftIcon: 'mdi-file-chart-outline',
          href: '#'
        },
        {
          text: 'Audit log',
          leftIcon: 'mdi-history',
          href: '#'
        }
      ]
    }
  }
];

// The root frame is the top-level list; useNavStack pushes child frames onto it.
const rootFrame = { items: navTree };

setup() {
  // useNavStack manages the drill-in depth; pass the root frame so currentFrame
  // returns it when the stack is empty. popNav / drillInAt are used directly.
  const { isRoot, currentFrame, popNav, drillInAt } = useNavStack(rootFrame);
  return { isRoot, currentFrame, popNav, drillInAt };
}

computed: {
  currentLevel() { return this.currentFrame; },
  canGoBack() { return !this.isRoot; }
}
`;

const HISTORY_DESCRIPTION =
  'CompliSpace has updated the Child Safe Module. The Justice Legislation Amendment ' +
  '(Sexual Offences and Other Matters) Act 2022 (Vic) amend the Crimes Act 1958 (Vic) ' +
  'to insert image-based sexual offences into the Act.';

const pageHistory = [
  { title: '15/11/2023 10:14 (Current version)', subtitle: HISTORY_DESCRIPTION, state: 'complete' },
  {
    title: '07/08/2023 16:03',
    subtitle: HISTORY_DESCRIPTION,
    titleButton: 'Restore',
    subtitleButton: 'Compare to current version'
  },
  {
    title: '07/06/2023 16:03',
    subtitle: HISTORY_DESCRIPTION,
    titleButton: 'Restore',
    subtitleButton: 'Compare to current version'
  }
];

const tags = [
  { label: 'Example tag', color: 'teal' },
  { label: 'Tag 1', color: 'teal' },
  { label: 'Tag 2', color: 'teal' },
  { label: 'Public Facing', color: 'teal' },
  { label: 'Another Tag', color: 'grey', removable: true },
  { label: 'Tag 3', color: 'teal' },
  { label: 'Tag 4', color: 'teal' },
  { label: 'Tag 5', color: 'teal' },
  { label: 'Another Tag with a really long name...', color: 'teal' }
];

const navTree = [
  {
    text: 'Page History',
    leftIcon: 'mdi-history',
    rightIcon: 'mdi-chevron-right',
    child: {
      title: 'Page History',
      headerIcon: 'mdi-history',
      body: { type: 'panel', component: PageHistoryTrackerPanel, props: { items: pageHistory } },
      footer: {
        variant: 'primary',
        label: 'Return to current version',
        leftIcon: 'mdi-backup-restore'
      }
    }
  },
  {
    text: 'Tags',
    leftIcon: 'mdi-tag-outline',
    rightIcon: 'mdi-chevron-right',
    child: {
      title: 'Tags',
      headerIcon: 'mdi-tag-outline',
      body: { type: 'panel', component: TagsPanel, props: { tags } }
    }
  },
  {
    text: 'Document Management',
    leftIcon: 'mdi-file-document-outline',
    rightIcon: 'mdi-chevron-right',
    child: {
      title: 'Document Management',
      headerIcon: 'mdi-file-document-outline',
      items: [
        { text: 'Policies', leftIcon: 'mdi-shield-check-outline', href: '#' },
        { text: 'Procedures', leftIcon: 'mdi-clipboard-text-outline', href: '#' },
        {
          text: 'Child Safety and Wellbeing Policy Framework 2024',
          leftIcon: 'mdi-file-document-outline',
          href: '#'
        }
      ]
    }
  },
  {
    text: 'Workflows',
    leftIcon: 'mdi-sitemap-outline',
    rightIcon: 'mdi-chevron-right',
    child: {
      title: 'Workflows',
      headerIcon: 'mdi-sitemap-outline',
      items: [{ text: 'Approvals', leftIcon: 'mdi-check-decagram-outline', href: '#' }]
    }
  },
  {
    text: 'Records, Audits and Compliance Management',
    leftIcon: 'mdi-clipboard-text-outline',
    rightIcon: 'mdi-chevron-right',
    child: {
      title: 'Records, Audits and Compliance Management',
      headerIcon: 'mdi-clipboard-text-outline',
      items: [
        { text: 'Annual report', leftIcon: 'mdi-file-chart-outline', href: '#' },
        { text: 'Audit log', leftIcon: 'mdi-history', href: '#' }
      ]
    }
  }
];

// The root frame is the top-level list; useNavStack pushes child frames onto it.
const rootFrame = { items: navTree };

export default {
  name: 'CSNavList',
  components: {
    UsageExample,
    'cs-nav-list': CSNavList,
    'cs-nav-list-items': CSNavListItems,
    'cs-nav-list-header': CSNavListHeader
  },

  setup() {
    const { isRoot, currentFrame, popNav, drillInAt } = useNavStack(rootFrame);
    return { isRoot, currentFrame, popNav, drillInAt };
  },

  data() {
    return {
      tab: 'Basic',
      tabs: ['Basic', 'Nested Items'],

      csNavItems: [
        { text: 'Page History', leftIcon: 'mdi-history', rightIcon: 'mdi-chevron-right' },
        { text: 'Tags', leftIcon: 'mdi-tag-outline', rightIcon: 'mdi-chevron-right' },
        {
          text: 'Document Management',
          leftIcon: 'mdi-file-document-outline',
          rightIcon: 'mdi-chevron-right'
        },
        { text: 'Workflows', leftIcon: 'mdi-sitemap-outline', rightIcon: 'mdi-chevron-right' }
      ]
    };
  },

  computed: {
    code() {
      if (this.tab === 'Nested Items') return SLOTS_CODE;
      return BASIC_CODE;
    },
    script() {
      return this.tab === 'Nested Items' ? SLOTS_SCRIPT : '';
    },
    currentLevel() {
      return this.currentFrame;
    },
    canGoBack() {
      return !this.isRoot;
    }
  },

  methods: {
    itemClick(index) {
      console.log('CS Nav List Item', index, 'item clicked');
    },
    leftIconClick(index) {
      console.log('CS Nav List Item', index, 'left icon clicked');
    },
    rightIconClick(index) {
      console.log('CS Nav List Item', index, 'right icon clicked');
    }
  }
};
</script>

<style lang="scss" scoped>
.cs-nav-list-demo__footer button {
  width: 100%;
}
</style>
